/* eslint-disable no-template-curly-in-string */
import { Menu, MenuItem } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import AccountCircle from '@material-ui/icons/AccountCircle';
import MenuIcon from '@material-ui/icons/Menu';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  PROFILE_PUFV_ASSESSOR_PEDAGOGICO,
  PROFILE_PUFV_COORDENADOR_LOCAL,
  PROFILE_PUFV_OPERACIONAL,
  PROFILE_PUFV_PROFESSOR,
  PROFILE_PUFV_PROFESSOR_GESTOR,
  PROFILE_PUFV_ASSESSOR_PEDAGOGICO_GESTOR,
} from '../../library/Profiles';
import { contratosRoute, projetosRoute } from '../../library/Routes';
import { openModal, closeModal } from '../../store/modules/ModalActions';
import { getElementID } from '../../utils/HTMLUtils';
import { AssessorEditModal } from '../AssessorEditModal';
import { CoordenadorEditModal } from '../CoordenadorEditModal';
import { fetchNotifications } from '../../store/modules/NotificationsActions';
import EditIcon from '../Icons/EditIcon';
import ExitToAppIcon from '../Icons/ExitToAppIcon';
import { ProfessorEditModal } from '../ProfessorEditModal';
import { toOpen } from '../LeftSidebar/LeftSidebarActions';
import styles from './Navbar.scss';
import NotificationIcon from 'assets/icons/NotificationIcon';
import Notifications from '../Notifications';
import { LogoProgramasEducacionais } from 'components/LogoProgramasEducacionais/LogoProgramasEducacionais';
import AuthService from 'services/Auth/AuthService';
import { AlertCard } from 'components/AlertCard/AlertCard';
import { loadAssessorByCPF } from 'store/modules/AssessoresActions';

const rootClassName = styles.rootClassName;
const rootElementId = 'navbar';

const assessorAutoModalKey = (cpf) => `assessor:auto:${cpf}`;
const assessorManualModalKey = (cpf) => `assessor:manual:${cpf}`;

const Navbar = () => {
  const [profileMenuOpened, setProfileMenuOpened] = useState(false);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const [anchorNotificationEl, setAnchorNotificationEl] = useState(null);
  const [showAlertCard, setShowAlertCard] = useState(false);
  const [, setBadgeCount] = useState(0);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { logout } = AuthService();

  const {
    auth,
    notifications,
    languageValues,
    assessores,
  } = useSelector((state) => ({
    auth: state.auth,
    notifications: state.notifications.notifications,
    languageValues: state.languages.languageValues,
    currentPage: state.notifications.currentPage,
    totalPages: state.notifications.totalPages,
    assessores: state.assessores,
  }));

  const modalsState = useSelector((state) => state.modals || state.modal || []);
  const getModalsArray = (ms) =>
    Array.isArray(ms) ? ms : ms?.items || ms?.stack || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isModalOpen = (key) =>
    !!getModalsArray(modalsState)?.find?.((m) => m?.key === key);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'notificationCount') {
        setBadgeCount(parseInt(event.newValue, 10));
      }
    };
    window.addEventListener('storage', handleStorage);
    const notificationCount = parseInt(localStorage.getItem('notificationCount'), 10) || 0;
    setBadgeCount(notificationCount);

    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const notificationCount = notifications.length;
    setBadgeCount(notificationCount);
    localStorage.setItem('notificationCount', notificationCount.toString());
  }, [notifications]);

  const toOpenProfileMenu = (event) => {
    setProfileMenuOpened(true);
    setProfileMenuAnchorEl(event.currentTarget);
  };
  const toCloseProfileMenu = () => setProfileMenuOpened(false);

  const toOpenEditModal = useCallback(() => {
    const { cpf, perfil } = auth.userData.profile;

    switch (perfil) {
      case PROFILE_PUFV_PROFESSOR.id:
      case PROFILE_PUFV_PROFESSOR_GESTOR.id:
        dispatch(openModal(ProfessorEditModal, { cpf }));
        break;

      case PROFILE_PUFV_COORDENADOR_LOCAL.id:
        dispatch(openModal(CoordenadorEditModal, { cpf }));
        break;

      case PROFILE_PUFV_ASSESSOR_PEDAGOGICO.id:
      case PROFILE_PUFV_ASSESSOR_PEDAGOGICO_GESTOR.id: {
        const autoKey = assessorAutoModalKey(cpf);
        if (isModalOpen(autoKey)) {
          // Evita 2 modais simultâneos
          dispatch(closeModal(autoKey));
        }
        dispatch(openModal(AssessorEditModal, { cpf, modalKey: assessorManualModalKey(cpf) }));
        break;
      }

      default:
        break;
    }
    toCloseProfileMenu();
  }, [auth.userData, dispatch, isModalOpen]);

  const toHomePage = () => {
    const perfil = auth.userData.perfil;
    if (perfil === PROFILE_PUFV_OPERACIONAL.id) history.push(contratosRoute.path);
    else history.push(projetosRoute.path);
  };

  const toOpenNotificationsMenu = (event) => {
    setAnchorNotificationEl(event.currentTarget);
    dispatch(fetchNotifications());
  };
  const toCloseNotificationsMenu = () => {
    setAnchorNotificationEl(null);
    dispatch(fetchNotifications());
  };

  const shouldDisplayBadge = () => {
    const localStorageCount = parseInt(localStorage.getItem('notificationCount'), 10);
    return notifications.length > 0 || localStorageCount > 0;
  };

  const AllowedRoutes = useMemo(
    () => [
      '/programaseducacionais/admin/projetos/pufv',
      '/programaseducacionais/admin/professores',
      '/programaseducacionais/admin/assessores',
      '/programaseducacionais/admin/coordenadores',
      '/programaseducacionais/admin/escolas',
      '/programaseducacionais/admin/empresas',
      '/programaseducacionais/admin/contratos',
      '/programaseducacionais/admin/relatorios',
      '/programaseducacionais/admin/materiais-pedagogicos',
      '/programaseducacionais/admin/materiais-pedagogicos/uniao-faz-a-vida',
      '/programaseducacionais/admin/materiais-pedagogicos/cooperativas-escolares',
      '/programaseducacionais/admin/materiais-pedagogicos/financas-na-mochila',
      '/programaseducacionais/admin/agenda-assessores-pedagogicos',
      '/programaseducacionais/admin/dashboard',
    ],
    []
  );
  const checkRoutes = useMemo(() =>
    AllowedRoutes.includes(location.pathname),
    [AllowedRoutes, location.pathname]);

  useEffect(() => {
    const cpf = auth?.userData?.profile?.cpf;
    if (!cpf) return;

    dispatch(
      loadAssessorByCPF(
        cpf,
        () => {
          sessionStorage.setItem(`assessorFetchDone:${cpf}`, "true");
        },
        () => {
          console.error("Erro ao chamar os dados do assessor");
        }
      )
    );
  }, [auth?.userData?.profile?.cpf, dispatch]);

  const deriveFromBackendCities = (editing) => {
    const cidadesBackend = Array.isArray(editing?.cidades) ? editing.cidades : [];
    const idEstadoFromCities =
      cidadesBackend.find((c) => typeof c.idEstado === 'number')?.idEstado ??
      editing?.idEstado ??
      -1;

    const nomesCidades =
      idEstadoFromCities > -1
        ? cidadesBackend
          .filter(
            (c) =>
              c &&
              c.idEstado === idEstadoFromCities &&
              (c.nomeCidade ?? '').trim().length > 0
          )
          .map((c) => (c.nomeCidade || '').trim().toUpperCase())
        : [];

    return {
      idEstado: Number(idEstadoFromCities),
      cidadesSelecionadas:
        Array.isArray(editing?.cidadesSelecionadas) &&
          editing.cidadesSelecionadas.filter((n) => (n ?? '').trim().length > 0)
            .length > 0
          ? editing.cidadesSelecionadas
          : nomesCidades,
    };
  };

  useEffect(() => {
    const cpf = auth?.userData?.profile?.cpf;
    const perfil = auth?.userData?.profile?.perfil;

    if (!cpf) {
      return;
    }

    const isAssessor =
      perfil === PROFILE_PUFV_ASSESSOR_PEDAGOGICO.id ||
      perfil === PROFILE_PUFV_ASSESSOR_PEDAGOGICO_GESTOR.id;

    if (!isAssessor) {
      return;
    }

    const editing = assessores?.assessorEditing ?? {};

    const backendLoaded = !!editing?.cpf;
    if (!backendLoaded) {
      return;
    }

    const normalized = { ...editing, ...deriveFromBackendCities(editing) };

    const idEstado = Number(normalized?.idEstado ?? -1);
    const cidades = Array.isArray(normalized?.cidadesSelecionadas)
      ? normalized.cidadesSelecionadas.filter(n => (n ?? "").trim().length > 0)
      : [];

    const missing = !(Number.isFinite(idEstado) && idEstado >= 0) || cidades.length === 0;
    const autoKey = assessorAutoModalKey(cpf);
    const openedAutoKey = `assessorModalAutoOpened:${cpf}`;

    if (missing) {
      if (!sessionStorage.getItem(openedAutoKey)) {
        dispatch(openModal(AssessorEditModal, { cpf, modalKey: autoKey }));
        sessionStorage.setItem(openedAutoKey, "true");
      } 
    } else {
      if (isModalOpen(autoKey)) {
        dispatch(closeModal(autoKey));
      }
    }
  }, [
    assessores?.assessorEditing,
    auth?.userData?.profile?.cpf,
    auth?.userData?.profile?.perfil,
    dispatch,
    isModalOpen
  ]);

  useEffect(() => {
    setShowAlertCard(AllowedRoutes.includes(location.pathname));
  }, [location.pathname, AllowedRoutes]);

  useEffect(() => {
    if (checkRoutes) {
      setShowAlertCard(true);
    } else {
      setShowAlertCard(false);
    }
  }, [checkRoutes]);

  if (!auth.userPermissions.navbar.view) return null;

  return (
    <>
      <AppBar className={rootClassName} id={getElementID(rootElementId, 'container')}>
        <Toolbar className={`${rootClassName}-toolbar`}>
          <div className={`${rootClassName}-left-toolbar`}>
            <IconButton
              id={getElementID(rootElementId, 'left-menu-toggle')}
              className={`${rootClassName}-icon-button`}
              aria-label="Menu"
              onClick={() => dispatch(toOpen())}
            >
              <MenuIcon className={`${rootClassName}-menu-icon`} />
            </IconButton>

            <button
              id={getElementID(rootElementId, 'logo')}
              className={`${rootClassName}-logo-pufv-button`}
              onClick={toHomePage}
            >
              <LogoProgramasEducacionais />
            </button>
          </div>

          <div className={`${rootClassName}-right-toolbar`}>

            <IconButton onClick={toOpenNotificationsMenu} style={{ cursor: 'pointer' }}>
              <NotificationIcon />
              {shouldDisplayBadge() && <div className="notification-badge" />}
            </IconButton>

            <IconButton
              id={getElementID(rootElementId, 'right-menu-toggle')}
              className={`${rootClassName}-icon-button`}
              aria-label="Menu"
              onClick={toOpenProfileMenu}
            >
              <AccountCircle className={`${rootClassName}-menu-icon`} />
            </IconButton>

            <Menu
              anchorEl={profileMenuAnchorEl}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={profileMenuOpened}
              onClose={toCloseProfileMenu}
            >
              {auth.userData?.profile.nome && (
                <MenuItem
                  id={getElementID(rootElementId, 'right-menu-item-name')}
                  className={`${rootClassName}-profile-menu-item`}
                  disabled
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: languageValues.profileMenuItemHello
                        .split('${1}')
                        .join(auth.userData.profile.nome.split(' ')[0]),
                    }}
                  />
                </MenuItem>
              )}

              {auth.userPermissions.navbar.profile.edit && (
                <MenuItem
                  id={getElementID(rootElementId, 'right-menu-item-profile')}
                  className={`${rootClassName}-profile-menu-item`}
                  onClick={toOpenEditModal}
                >
                  <EditIcon className={`${rootClassName}-profile-menu-icon`} />
                  {languageValues.profileMenuItemEdit}
                </MenuItem>
              )}

              <MenuItem
                id={getElementID(rootElementId, 'right-menu-item-logout')}
                className={`${rootClassName}-profile-menu-item`}
                onClick={() => logout()}
              >
                <ExitToAppIcon className={`${rootClassName}-profile-menu-icon`} />
                <span className={`${rootClassName}-profile-menu-item-text`}>
                  {languageValues.profileMenuItemLogout}
                </span>
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>

        <Notifications
          anchorNotificationEl={anchorNotificationEl}
          toCloseNotificationsMenu={toCloseNotificationsMenu}
        />
      </AppBar>

      <div
        id="container_box-alerta"
        className={`container_box-alerta ${showAlertCard ? 'alerta-ativo' : 'alerta-inativo'}`}
      >
        {showAlertCard && <AlertCard showAlert={false} />}
      </div>
    </>
  );
};

export default Navbar;